import dayjs from 'dayjs'

export function getCurrentQuarterRange(): [string, string] {
  const now = dayjs()
  const currentMonth = now.month()
  const startMonth = Math.floor(currentMonth / 3) * 3
  const start = now.month(startMonth).startOf('month')
  const end = now.month(startMonth + 2).endOf('month')
  return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
}

// A selected end date is "historical" when it is a valid date that is neither
// the current quarter end nor today/future — i.e. a past point-in-time view.
export function isHistoricalEndDate(
  endDateValue: string | null | undefined
): boolean {
  const endDate = dayjs(endDateValue).format('YYYY-MM-DD')
  const isQuarterEnd = endDate === getCurrentQuarterRange()[1]
  const isFutureOrToday =
    dayjs(endDate).isSame(dayjs(), 'day') ||
    dayjs(endDate).isAfter(dayjs(), 'day')
  const isValidEndDate = dayjs(endDateValue).isValid()
  return !isQuarterEnd && !isFutureOrToday && isValidEndDate
}

export function getQuarterRangeForYear(year: number, quarter: 1 | 2 | 3 | 4) {
  const startMonth = (quarter - 1) * 3
  return [
    dayjs().year(year).month(startMonth).startOf('month'),
    dayjs()
      .year(year)
      .month(startMonth + 2)
      .endOf('month'),
  ]
}

export function getHalfYearRangeForYear(year: number, half: 1 | 2) {
  const startMonth = half === 1 ? 0 : 6
  return [
    dayjs().year(year).month(startMonth).startOf('month'),
    dayjs()
      .year(year)
      .month(startMonth + 5)
      .endOf('month'),
  ]
}

export function getMTD() {
  return [dayjs().startOf('month'), dayjs()]
}

export function getQTD() {
  const now = dayjs()
  const currentMonth = now.month()
  const startMonth = Math.floor(currentMonth / 3) * 3
  const startOfQuarter = now.month(startMonth).startOf('month')
  return [startOfQuarter, now]
}

export function getYTD() {
  return [dayjs().startOf('year'), dayjs()]
}

export const LTD_START_DATE = '2023-01-01'

// The mobile calendar renders every month between its min and max, so cap the window.
export const DEFAULT_CALENDAR_MONTHS_BACK = 12

/** Earliest month shown: `monthsBack` months before the selection start (or today), never before `min`. */
export function getVisibleCalendarMin(
  min: dayjs.Dayjs,
  max: dayjs.Dayjs,
  value: [dayjs.Dayjs, dayjs.Dayjs] | null,
  monthsBack: number,
  today: dayjs.Dayjs = dayjs()
): dayjs.Dayjs {
  const fallback = today.isAfter(max) ? max : today
  const anchor = value?.[0] && !value[0].isAfter(max) ? value[0] : fallback
  const windowStart = anchor.subtract(monthsBack, 'month').startOf('month')
  return windowStart.isBefore(min) ? min : windowStart
}

export function getRangePresets() {
  const currentYear = dayjs().year()
  const previousYear = currentYear - 1
  const currentMonth = dayjs().month()
  const currentQuarter = Math.floor(currentMonth / 3)

  // Only include quarters that have started
  const quarterLabels = ['Q1', 'Q2', 'Q3', 'Q4']
  const currentYearQuarters = quarterLabels
    .map((label, i) =>
      i <= currentQuarter
        ? {
            label: `${label} ${currentYear}`,
            value: getQuarterRangeForYear(
              currentYear,
              (i + 1) as 1 | 2 | 3 | 4
            ) as [dayjs.Dayjs, dayjs.Dayjs],
          }
        : null
    )
    .filter(Boolean) as { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[]

  // Only include H2 if current month >= 6 (July or later)
  const currentYearHalves = [
    {
      label: `H1 ${currentYear}`,
      value: getHalfYearRangeForYear(currentYear, 1) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    ...(currentMonth >= 6
      ? [
          {
            label: `H2 ${currentYear}`,
            value: getHalfYearRangeForYear(currentYear, 2) as [
              dayjs.Dayjs,
              dayjs.Dayjs,
            ],
          },
        ]
      : []),
  ]

  return [
    ...currentYearQuarters,
    ...currentYearHalves,
    { label: 'MTD', value: getMTD() as [dayjs.Dayjs, dayjs.Dayjs] },
    { label: 'QTD', value: getQTD() as [dayjs.Dayjs, dayjs.Dayjs] },
    { label: 'YTD', value: getYTD() as [dayjs.Dayjs, dayjs.Dayjs] },
    {
      label: 'LTD',
      value: [dayjs(LTD_START_DATE), dayjs()] as [dayjs.Dayjs, dayjs.Dayjs],
    },
    // Previous year quarters and halves (show all)
    {
      label: `Q1 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 1) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `Q2 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 2) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `Q3 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 3) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `Q4 ${previousYear}`,
      value: getQuarterRangeForYear(previousYear, 4) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `H1 ${previousYear}`,
      value: getHalfYearRangeForYear(previousYear, 1) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
    {
      label: `H2 ${previousYear}`,
      value: getHalfYearRangeForYear(previousYear, 2) as [
        dayjs.Dayjs,
        dayjs.Dayjs,
      ],
    },
  ]
}

export function findMatchingPreset(
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
): string | null {
  const presets = getRangePresets()

  for (const preset of presets) {
    const [presetStart, presetEnd] = preset.value
    if (
      startDate.isSame(presetStart, 'day') &&
      endDate.isSame(presetEnd, 'day')
    ) {
      return preset.label
    }
  }

  return null
}
